import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Circle, Loader2, X, XCircle } from "lucide-react";
import { useAdminJobs } from "../../contexts/AdminJobsContext";
import DownloadTrayIcon from "../icons/DownloadTrayIcon";

function stepIcon(status, variant) {
  const spin = variant === "site" ? "text-[#556652]" : "text-admin-primary";
  const done = variant === "site" ? "text-emerald-600" : "text-emerald-600";
  if (status === "done") return <CheckCircle2 size={16} className={done} aria-hidden />;
  if (status === "active") return <Loader2 size={16} className={`animate-spin ${spin}`} aria-hidden />;
  if (status === "error") return <XCircle size={16} className="text-rose-600" aria-hidden />;
  return <Circle size={16} className="text-[#b5aea2]" aria-hidden />;
}

/**
 * Tiến trình kiểu Chrome: nút + popup danh sách.
 * variant="site" — cùng phong cách nút header trang công khai (cream / xanh ô liu).
 * variant="admin" — giao diện panel admin (legacy).
 */
export default function AdminJobsTray({ variant = "site", size = "lg" }) {
  const { jobs, dismissJob, clearFinished, hasRunningJob } = useAdminJobs();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const runningCount = jobs.filter((j) => j.status === "running").length;
  const errorCount = jobs.filter((j) => j.status === "error").length;
  const showBadge = runningCount > 0 || errorCount > 0;

  const isSite = variant === "site";
  const btnClass = isSite
    ? size === "lg"
      ? "relative inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#e1d4c0] bg-[#fffaf1] text-[#556652] shadow-[0_8px_18px_rgba(60,45,30,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#cdbd9f] hover:bg-[#f7f1e6] hover:shadow-[0_12px_26px_rgba(60,45,30,0.12)]"
      : size === "md"
        ? "relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#e1d4c0] bg-[#fffaf1] text-[#556652] shadow-[0_6px_14px_rgba(60,45,30,0.08)] transition hover:border-[#cdbd9f] hover:bg-[#f7f1e6]"
        : "relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e1d4c0] bg-[#fffaf1] text-[#556652] shadow-sm transition hover:border-[#cdbd9f] hover:bg-[#f7f1e6]"
    : "relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text";

  const iconSize = size === "lg" ? 20 : size === "md" ? 18 : 16;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const panelClass = isSite
    ? "absolute right-0 top-[calc(100%+14px)] z-[100002] w-[min(100vw-1.5rem,22rem)] overflow-hidden rounded-[22px] border border-[#e5d9c7] bg-[#fffaf1] shadow-[0_24px_60px_rgba(0,0,0,0.18)] ring-1 ring-black/5"
    : "absolute right-0 top-full z-[99999] mt-2 w-[min(100vw-1.5rem,22rem)] overflow-hidden rounded-2xl border-2 border-admin-border bg-admin-surface shadow-[0_24px_80px_rgba(15,23,42,0.18)] ring-1 ring-black/5";

  const headClass = isSite
    ? "flex items-center justify-between gap-2 border-b border-[#eee5d8] bg-[linear-gradient(180deg,#697961_0%,#556652_100%)] px-3 py-2.5"
    : "flex items-center justify-between gap-2 border-b-2 border-admin-border bg-admin-muted px-3 py-2.5";

  const headTitle = isSite ? "text-[11px] font-extrabold uppercase tracking-wider text-white/90" : "text-[11px] font-extrabold uppercase tracking-wider text-admin-textMuted";

  const cardClass = isSite
    ? "rounded-2xl border border-[#e2d6c5] bg-white px-3 py-2.5 shadow-sm"
    : "rounded-xl border border-admin-border bg-white px-3 py-2.5 shadow-sm";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={btnClass}
        aria-label="Tiến trình"
        title="Tiến trình"
      >
        {hasRunningJob ? (
          <Loader2 size={iconSize} className={isSite ? "animate-spin text-[#556652]" : "animate-spin text-admin-primary"} aria-hidden />
        ) : (
          <DownloadTrayIcon size={iconSize} />
        )}
        {showBadge && (
          <span
            className={
              isSite
                ? "absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#556652] px-0.5 text-[10px] font-black text-white shadow-md ring-2 ring-white"
                : "absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-admin-primary px-0.5 text-[9px] font-black text-white ring-2 ring-white"
            }
          >
            {runningCount || errorCount}
          </span>
        )}
      </button>

      {open && (
        <div className={panelClass}>
          <div className={headClass}>
            <p className={headTitle}>Tiến trình</p>
            <div className="flex items-center gap-1">
              {jobs.length > 0 ? (
                <button
                  type="button"
                  onClick={() => clearFinished()}
                  className={
                    isSite
                      ? "rounded-lg px-2 py-1 text-[10px] font-bold text-white/85 hover:bg-white/10 hover:text-white"
                      : "rounded-lg px-2 py-1 text-[10px] font-bold text-admin-textMuted hover:bg-white hover:text-admin-text"
                  }
                >
                  Xoá xong
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={
                  isSite
                    ? "rounded-lg p-1 text-white/80 hover:bg-white/10 hover:text-white"
                    : "rounded-lg p-1 text-admin-textMuted hover:bg-white hover:text-admin-text"
                }
                aria-label="Đóng"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="max-h-[min(70vh,420px)] overflow-y-auto p-3">
            {jobs.length === 0 ? (
              <div
                className={
                  isSite
                    ? "rounded-2xl border border-[#e2d6c5] bg-white px-4 py-6 text-center shadow-sm"
                    : "rounded-xl border border-admin-border bg-white px-4 py-6 text-center shadow-sm"
                }
              >
                <p className={`text-sm font-extrabold ${isSite ? "text-[#2f352c]" : "text-admin-text"}`}>
                  Chưa có tiến trình
                </p>
                <p className={`mt-1 text-[11px] font-medium ${isSite ? "text-[#7b7569]" : "text-admin-textMuted"}`}>
                  Khi bạn thêm/chỉnh sửa địa điểm, tiến trình sẽ hiện ở đây.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs
                  .slice()
                  .reverse()
                  .map((job) => (
                    <div key={job.id} className={cardClass}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className={`truncate text-sm font-extrabold ${isSite ? "text-[#2f352c]" : "text-admin-text"}`}>{job.title}</p>
                          {job.subtitle ? (
                            <p className={`mt-0.5 truncate text-[11px] font-medium ${isSite ? "text-[#7b7569]" : "text-admin-textMuted"}`}>{job.subtitle}</p>
                          ) : null}
                          {job.status === "error" && job.error ? (
                            <p className="mt-1 text-[11px] font-semibold leading-snug text-rose-600">{job.error}</p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => dismissJob(job.id)}
                          className={
                            isSite
                              ? "shrink-0 rounded-lg p-1 text-[#7b7569] hover:bg-[#f7f1e6] hover:text-[#2f352c]"
                              : "shrink-0 rounded-lg p-1 text-admin-textMuted hover:bg-admin-muted hover:text-admin-text"
                          }
                          aria-label="Bỏ khỏi danh sách"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <ul className={`mt-2 space-y-1.5 border-t pt-2 ${isSite ? "border-[#eee5d8]" : "border-admin-border"}`}>
                        {job.steps.map((s, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-[11px]">
                            <span className="mt-0.5 shrink-0">{stepIcon(s.status, variant)}</span>
                            <span
                              className={
                                s.status === "active"
                                  ? isSite
                                    ? "font-bold text-[#556652]"
                                    : "font-bold text-admin-primaryHover"
                                  : s.status === "done"
                                    ? isSite
                                      ? "font-semibold text-[#7b7569]"
                                      : "font-semibold text-admin-textMuted"
                                    : s.status === "error"
                                      ? "font-semibold text-rose-700"
                                      : isSite
                                        ? "text-[#b5aea2]"
                                        : "text-admin-textMuted"
                              }
                            >
                              {s.label}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
